import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

//components

import ArchiveHousehold from './ArchiveHousehold';
import NavBarContainer from '@containers/common/NavBarContainer';
import { Modal as GIModal } from 'app-components';

//hooks

import useArchiveHousehold from './useArchiveHousehold';
import type { ArchiveSearchDetails } from './useArchiveHousehold';
import useDisclosure from '@hooks/useDisclosure';

const ArchiveHouseholdContainer = () => {

    const { data, isError, archiveHousehold, searchByHouseholdId, unArchiveHousehold } = useArchiveHousehold();
    const [ selectedHouseholdDetails, setSelectedHouseholdDetails ] = useState<ArchiveSearchDetails | null>(null);
    const { isOpen: showArchiveErrorModal, onOpen: openArchiveErrorModal, onClose: closeArchiveErrorModal } = useDisclosure();
    const { isOpen: showArchiveConfirmModal, onOpen: openArchiveConfirmModal, onClose: closeArchiveConfirmModal } = useDisclosure();
    const { isOpen: showArchiveSuccessModal, onOpen: openArchiveSuccessmModal, onClose: closeArchiveSuccessModal } = useDisclosure();
    const { isOpen: showUnArchiveConfirmModal, onOpen: openUnArchiveConfirmModal,onClose: closeUnArchiveConfirmModal } = useDisclosure();
    const { isOpen: showUnArchiveSuccessModal, onOpen: openUnArchiveSuccessmModal, onClose: closeUnArchiveSuccessModal } = useDisclosure();
    const lastHouseholdId = useRef<string | null>(null);

    const onSearch = (householdId: string) => {
        lastHouseholdId.current = householdId;
        searchByHouseholdId(householdId);
    };

    const onArchive = (item: ArchiveSearchDetails) => {
        setSelectedHouseholdDetails(item);
        openArchiveConfirmModal();
    };

    const onUnArchive = (item: ArchiveSearchDetails) => {
        setSelectedHouseholdDetails(item);
        openUnArchiveConfirmModal();
    };

    const onArchiveConfirm = async () => {
        closeArchiveConfirmModal();
        const resp = await archiveHousehold(selectedHouseholdDetails?.encHouseholdId);
        if (resp.errorMessage && resp.errorMessage.length > 0) {
            openArchiveErrorModal();
        } else {
            openArchiveSuccessmModal();
        }
    };

    const onUnArchiveConfirm = async () => {
        closeUnArchiveConfirmModal();
        const resp = await unArchiveHousehold(selectedHouseholdDetails?.encHouseholdId);
        if (resp.errorMessage && resp.errorMessage.length > 0) {
            openArchiveErrorModal();
        } else {
            openUnArchiveSuccessmModal();
        }
    };

    const onArchiveSuccess = () => {
        closeArchiveSuccessModal();
        searchByHouseholdId(lastHouseholdId.current);
    };

    const onUnarchiveSuccess = () => {
        closeUnArchiveSuccessModal();
        searchByHouseholdId(lastHouseholdId.current);
    };

    const renderConfirmMessages = () => {

        if (selectedHouseholdDetails) {
            const {
                applicationYears,
                enrollmentYears,
                isInboxmsgPresent,
                AgentDesignated
            } = selectedHouseholdDetails;

            return (
                <ol>
                    { isInboxmsgPresent && <li><FormattedMessage id='archive.hh.results.has.inbox_msgs' /></li> }
                    { applicationYears && applicationYears.length > 0 && <li><FormattedMessage id='archive.hh.results.has.applications' /></li> }
                    { enrollmentYears && enrollmentYears.length > 0 && <li><FormattedMessage id='archive.hh.results.has.enrollments' /></li> }
                    { AgentDesignated && <li><FormattedMessage id='archive.hh.results.has.agent_designations' /></li> }
                </ol>
            );
        }
    };

    const ArchiveConfirmModal = () => {

        let hasItemsAttached = false;
        if (selectedHouseholdDetails) {
            const {
                applicationYears,
                enrollmentYears,
                isInboxmsgPresent,
                AgentDesignated
            } = selectedHouseholdDetails;

            hasItemsAttached = (applicationYears && applicationYears.length > 0) ||
                (enrollmentYears && enrollmentYears.length > 0) ||
                isInboxmsgPresent ||
                !!AgentDesignated;
        }

        if (showArchiveConfirmModal) {
            return (
                <GIModal id='archive_confirm_modal'>
                    <GIModal.Header id='archive_confirm_modal_title' titleId='archive.hh.confirm.title' onHide={closeArchiveConfirmModal} />
                    <GIModal.Body>
                        <div className='msg'>
                            <div className='subTitle'>
                                <FormattedMessage id='archive.hh.confirm.title.sub' />
                            </div>
                            <div className='items'>
                                { hasItemsAttached && renderConfirmMessages() }
                            </div>
                        </div>
                    </GIModal.Body>
                    <GIModal.Controls>
                        <button className="usa-button gi-button gi-button__secondary" type="button" onClick={closeArchiveConfirmModal}><FormattedMessage id='archive.hh.confirm.btn.cancel' /></button>
                        <button className="usa-button gi-button gi-button__primary" type="button" onClick={onArchiveConfirm}><FormattedMessage id='archive.hh.confirm.btn.confirm' /></button>
                    </GIModal.Controls>
                </GIModal>
            );
        }

        return null;
    };

    const ArchiveSuccessModal = () => {
        if (showArchiveSuccessModal) {
            return (
                <GIModal id='archive_success_modal'>
                    <GIModal.Header id='archive_success_modal_title' titleId='archive.hh.success.title' onHide={onArchiveSuccess} />
                    <GIModal.Body>
                        <div className='msg'>
                            <div className='subTitle'>
                                <FormattedMessage id='archive.hh.success.title.sub' />
                            </div>
                        </div>
                    </GIModal.Body>
                    <GIModal.Controls>
                        <button className="usa-button gi-button gi-button__primary" type="button" onClick={onArchiveSuccess}><FormattedMessage id='archive.hh.success.btn.done' /></button>
                    </GIModal.Controls>
                </GIModal>
            );
        }

        return null;
    };

    const UnArchiveConfirmModal = () => {

        let hasItemsAttached = false;
        if (selectedHouseholdDetails) {
            const {
                applicationYears,
                enrollmentYears,
                isInboxmsgPresent,
                AgentDesignated
            } = selectedHouseholdDetails;

            hasItemsAttached = (applicationYears && applicationYears.length > 0) ||
              (enrollmentYears && enrollmentYears.length > 0) ||
              isInboxmsgPresent ||
              !!AgentDesignated;
        }

        if (showUnArchiveConfirmModal) {
            return (
                <GIModal id='unarchive_confirm_modal'>
                    <GIModal.Header id='unarchive_confirm_modal_title' titleId='archive.hh.un_archive.confirm.title' onHide={closeUnArchiveConfirmModal} />
                    <GIModal.Body>
                        <div className='msg'>
                            <div className='subTitle'>
                                <FormattedMessage id='archive.hh.un_archive.confirm.title.sub' />
                            </div>
                            <div className='items'>
                                { hasItemsAttached && renderConfirmMessages() }
                            </div>
                        </div>
                    </GIModal.Body>
                    <GIModal.Controls>
                        <button className="usa-button gi-button gi-button__secondary" type="button" onClick={closeUnArchiveConfirmModal}><FormattedMessage id='archive.hh.confirm.btn.cancel' /></button>
                        <button className="usa-button gi-button gi-button__primary" type="button" onClick={onUnArchiveConfirm}><FormattedMessage id='archive.hh.confirm.btn.confirm' /></button>
                    </GIModal.Controls>
                </GIModal>
            );
        }

        return null;
    };

    const UnArchiveSuccessModal = () => {
        if (showUnArchiveSuccessModal) {
            return (
                <GIModal id='unarchive_success_modal'>
                    <GIModal.Header id='unarchive_success_modal_title' titleId='archive.hh.un_archive.success.title' onHide={onUnarchiveSuccess} />
                    <GIModal.Body>
                        <div className='msg'>
                            <div className='subTitle'>
                                <FormattedMessage id='archive.hh.un_archive.success.title.sub' />
                            </div>
                        </div>
                    </GIModal.Body>
                    <GIModal.Controls>
                        <button className="usa-button gi-button gi-button__primary" type="button" onClick={onUnarchiveSuccess}><FormattedMessage id='archive.hh.success.btn.done' /></button>
                    </GIModal.Controls>
                </GIModal>
            );
        }

        return null;
    };

    const ArchiveErrorModal = () => {
        if (showArchiveErrorModal) {
            return (
                <GIModal id='archive_error_modal'>
                    <GIModal.Header id='archive_error_modal_title' titleId='archive.hh.archive.error' onHide={closeArchiveErrorModal} />
                    <GIModal.Body>
                        <div className='msg'>
                            <div className='subTitle'>
                                <FormattedMessage id='archive.hh.archive.error.sub' />
                            </div>
                        </div>
                    </GIModal.Body>
                    <GIModal.Controls>
                        <button className="usa-button gi-button gi-button__primary" type="button" onClick={closeArchiveErrorModal}><FormattedMessage id='archive.hh.success.btn.done' /></button>
                    </GIModal.Controls>
                </GIModal>
            );
        }

        return null;
    };

    return (
        <main className='usa-layout-docs usa-section gi-main-section' id='archiveHousehold'>
            <div className='grid-container grid-widescreen gi-grid-container'>
                <div className='grid-row grid-gap gi-wrapper'>
                    <div className='grid-col-12'>
                        <div className='grid-row'>
                            <NavBarContainer navBarClass='grid-col-12 menu__top-nav'/>
                        </div>
                    </div>
                    <div className='grid-col-12'>
                        <div className='grid-row'>
                            <div className='desktop:grid-col-9' id='main-content'>
                                <ArchiveHousehold data={data} isError={isError} onArchive={onArchive} onSearch={onSearch} onUnArchive={onUnArchive} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            { showArchiveConfirmModal && <ArchiveConfirmModal /> }
            { showArchiveSuccessModal && <ArchiveSuccessModal /> }
            { showUnArchiveConfirmModal && <UnArchiveConfirmModal /> }
            { showUnArchiveSuccessModal && <UnArchiveSuccessModal /> }
            { showArchiveErrorModal && <ArchiveErrorModal /> }
        </main>
    );
};

export default ArchiveHouseholdContainer;